import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea, Select } from '@components';
import { ApiEndpoint } from '../../types';

interface ApiEndpointsSectionProps {
  apiEndpoints: ApiEndpoint[];
  updateApiEndpoint: (id: string, field: keyof ApiEndpoint, value: string) => void;
  addApiEndpoint: () => void;
  removeApiEndpoint: (id: string) => void;
  addHeader: (endpointId: string) => void;
  removeHeader: (endpointId: string, index: number) => void;
  updateHeader: (endpointId: string, index: number, field: 'name' | 'value', value: string) => void;
  extractedVariables?: string[];
  allAvailableVariables?: string[];
  urlPlaceholder?: string;
  requestBodyPlaceholder?: string;
  includePatch?: boolean;
}

const HTTP_METHODS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'DELETE', label: 'DELETE' },
];

export const ApiEndpointsSection: React.FC<ApiEndpointsSectionProps> = ({
  apiEndpoints,
  updateApiEndpoint,
  addApiEndpoint,
  removeApiEndpoint,
  addHeader,
  removeHeader,
  updateHeader,
  extractedVariables = [],
  allAvailableVariables,
  urlPlaceholder,
  requestBodyPlaceholder,
  includePatch = false,
}) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const methods = includePatch
    ? [...HTTP_METHODS, { value: 'PATCH', label: 'PATCH' }]
    : HTTP_METHODS;

  // Use allAvailableVariables if provided (e.g., from PostCallTab with post-call variables),
  // otherwise fall back to default behavior with caller_number and agent_name
  const variablesToDisplay =
    allAvailableVariables ||
    Array.from(new Set(['caller_number', 'agent_name', ...extractedVariables]));

  const insertVariableAtCursor = (endpointId: string, variableName: string) => {
    const textarea = textareaRefs.current[endpointId];
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const variableToken = `{{ ${variableName} }}`;

    const newValue = currentValue.substring(0, start) + variableToken + currentValue.substring(end);

    updateApiEndpoint(endpointId, 'body', newValue);

    // Set cursor position after inserted variable
    setTimeout(() => {
      const newCursorPos = start + variableToken.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);

    setOpenDropdown(null);
  };

  return (
    <div>
      <label
        htmlFor="api-endpoints"
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {t('agents.apiEndpointsMax5')}
      </label>
      <div className="space-y-4">
        {apiEndpoints.map(endpoint => (
          <div
            key={endpoint.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_minmax(200px,1fr)_minmax(320px,2fr)_auto]">
              <Select
                className="w-full"
                value={endpoint.method}
                onChange={value =>
                  updateApiEndpoint(endpoint.id, 'method', Array.isArray(value) ? value[0] : value)
                }
                options={methods}
              />
              <Input
                type="text"
                className="w-full"
                placeholder={t('agents.apiName')}
                value={endpoint.name}
                onChange={e => updateApiEndpoint(endpoint.id, 'name', e.target.value)}
              />
              <Input
                type="url"
                className="w-full"
                placeholder={urlPlaceholder || 'https://api.example.com/endpoint'}
                value={endpoint.url}
                onChange={e => updateApiEndpoint(endpoint.id, 'url', e.target.value)}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeApiEndpoint(endpoint.id)}
                className="justify-self-start sm:justify-self-end"
              >
                <span className="material-icons">delete</span>
              </Button>
            </div>

            <div>
              <label
                htmlFor={`headers-${endpoint.id}`}
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.headers')}
              </label>
              <div className="space-y-2">
                {endpoint.headers.map((header, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(200px,1fr)_minmax(300px,2fr)_auto]"
                  >
                    <Input
                      type="text"
                      className="w-full"
                      placeholder={t('agents.headerName')}
                      value={header.name}
                      onChange={e => updateHeader(endpoint.id, index, 'name', e.target.value)}
                    />
                    <Input
                      type="text"
                      className="w-full"
                      placeholder={t('agents.headerValue')}
                      value={header.value}
                      onChange={e => updateHeader(endpoint.id, index, 'value', e.target.value)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeHeader(endpoint.id, index)}
                      className="justify-self-start sm:justify-self-end"
                    >
                      <span className="material-icons">delete</span>
                    </Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => addHeader(endpoint.id)}>
                  <span className="material-icons">add</span>
                  {t('agents.addHeader')}
                </Button>
              </div>
            </div>

            {endpoint.method !== 'GET' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor={`request-body-${endpoint.id}`}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('agents.requestBody')}
                  </label>
                  {variablesToDisplay.length > 0 && (
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setOpenDropdown(openDropdown === endpoint.id ? null : endpoint.id)
                        }
                      >
                        <span className="material-icons text-sm mr-1">add_circle</span>
                        {t('agents.insertVariable')}
                      </Button>
                      {openDropdown === endpoint.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenDropdown(null)}
                            onKeyDown={e => e.key === 'Escape' && setOpenDropdown(null)}
                            role="button"
                            tabIndex={0}
                            aria-label="Close dropdown"
                          />
                          <div className="absolute right-0 mt-1 w-[480px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                            <div className="py-1">
                              {variablesToDisplay.map(variable => {
                                const translationKey = `agents.variables.${variable}`;
                                const hasTranslation = t(translationKey) !== translationKey;
                                const displayName = hasTranslation ? t(translationKey) : variable;

                                return (
                                  <button
                                    key={variable}
                                    onClick={() => insertVariableAtCursor(endpoint.id, variable)}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                  >
                                    <span>{displayName}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                      {`{{ ${variable} }}`}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <Textarea
                  ref={el => {
                    textareaRefs.current[endpoint.id] = el;
                  }}
                  id={`request-body-${endpoint.id}`}
                  className="w-full font-mono text-sm"
                  rows={8}
                  value={endpoint.body}
                  onChange={e => updateApiEndpoint(endpoint.id, 'body', e.target.value)}
                  placeholder={requestBodyPlaceholder || t('agents.requestBodyPlaceholder')}
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('agents.useVariableNameForDynamicContent')}
                </p>
              </div>
            )}
          </div>
        ))}

        {apiEndpoints.length < 5 && (
          <Button variant="gradient-multi" size="sm" onClick={addApiEndpoint}>
            <span className="material-icons">add</span>
            {t('agents.addApiEndpoint')}
          </Button>
        )}
      </div>
    </div>
  );
};
